// src/admin/pages/Bookings.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { Card } from "../ui/Card";
import { Badge } from "../ui/Badge";
import { Table } from "../ui/Table";

function overlaps(aStart, aEnd, bStart, bEnd) {
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  const aS = new Date(aStart).getTime();
  const aE = new Date(aEnd).getTime();
  const bS = new Date(bStart).getTime();
  const bE = new Date(bEnd).getTime();
  return aS <= bE && bS <= aE;
}

function toneForStatus(status, conflict) {
  const s = String(status || "").toLowerCase();
  if (s === "requested" && conflict) return "danger";
  if (s === "requested") return "warning";
  if (s === "approved") return "success";
  if (s === "active") return "info";
  if (s === "completed") return "neutral";
  if (s === "cancelled") return "neutral";
  return "neutral";
}

function labelForStatus(status, conflict) {
  const s = String(status || "").toLowerCase();
  if (s === "requested" && conflict) return "Conflict";
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "—";
}

function fullNameFromContact(c) {
  const first = c?.first_name || "";
  const last = c?.last_name || "";
  return `${first} ${last}`.trim() || "Unnamed";
}

export default function Bookings() {
  const [status, setStatus] = useState("All"); // UI label
  const [rowsRaw, setRowsRaw] = useState([]); // raw rows from DB
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);

  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadErr(null);

      try {
        const query = supabase
          .from("bookings")
          .select(
            `
            id,
            created_at,
            membership_id,
            vehicle_id,
            profile_id,
            created_by,
            start_date,
            end_date,
            status,
            pickup_location,
            notes,
            concierge,
            memberships:membership_id (
              id,
              tier,
              status,
              can_book,
              crm_contacts:contact_id (
                id,
                first_name,
                last_name,
                email,
                phone
              )
            ),
            vehicles:vehicle_id (
              id,
              name,
              category
            )
          `
          )
          .order("created_at", { ascending: false })
          .limit(500);

        const { data, error } = await query;
        if (error) throw error;

        if (!cancelled) setRowsRaw(data || []);
      } catch (e) {
        if (!cancelled) setLoadErr(e.message || "Failed to load bookings.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(() => {
    const desired = status === "All" ? null : String(status || "").toLowerCase();

    const filtered = rowsRaw.filter((b) =>
      desired ? String(b.status || "").toLowerCase() === desired : true
    );

    const blockers = rowsRaw.filter((b) =>
      ["approved", "active"].includes(String(b.status || "").toLowerCase())
    );

    return filtered.map((b) => {
      const memberContact = b?.memberships?.crm_contacts || null;
      const vehicle = b?.vehicles || null;

      const isRequested = String(b.status || "").toLowerCase() === "requested";
      const conflict = isRequested
        ? blockers.some(
            (x) =>
              x.vehicle_id === b.vehicle_id &&
              overlaps(b.start_date, b.end_date, x.start_date, x.end_date)
          )
        : false;

      return {
        __key: b.id,

        // If your Table row is a <button>, it likely calls row.onClick.
        // We keep it generic: provide __onClick and onClick (either could be used by your Table).
        __onClick: () => nav(`/admin/bookings/${b.id}`),
        onClick: () => nav(`/admin/bookings/${b.id}`),

        id: b.id,
        start_date: b.start_date,
        end_date: b.end_date,
        status: b.status,
        concierge: b.concierge,
        pickup_location: b.pickup_location,

        membership_id: b.membership_id,
        vehicle_id: b.vehicle_id,

        memberName: memberContact
          ? fullNameFromContact(memberContact)
          : b.membership_id
          ? "Member"
          : "—",
        memberEmail: memberContact?.email || "—",

        vehicleName: vehicle?.name || b.vehicle_id,
        conflict,
      };
    });
  }, [rowsRaw, status, nav]);

  const columns = [
    {
      key: "id",
      header: "Booking",
      width: "22%",
      cell: (r) => (
        <div className="cellTitle">
          <div className="cellTitle__top">{String(r.id).slice(0, 8)}</div>
          <div className="cellTitle__sub">
            {r.start_date} → {r.end_date}
          </div>
        </div>
      ),
    },
    {
      key: "member",
      header: "Member",
      width: "22%",
      cell: (r) =>
        r.membership_id ? (
          <span
            className="linkBtn"
            role="link"
            tabIndex={0}
            title={r.memberEmail}
            onClick={(e) => {
              e.stopPropagation();
              nav(`/admin/members/${r.membership_id}`);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                nav(`/admin/members/${r.membership_id}`);
              }
            }}
          >
            {r.memberName}
          </span>
        ) : (
          <span className="muted">—</span>
        ),
    },
    {
      key: "vehicle",
      header: "Vehicle",
      width: "26%",
      cell: (r) =>
        r.vehicle_id ? (
          <span
            className="linkBtn"
            role="link"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              nav(`/admin/vehicles/${r.vehicle_id}`);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                nav(`/admin/vehicles/${r.vehicle_id}`);
              }
            }}
          >
            {r.vehicleName}
          </span>
        ) : (
          <span className="muted">—</span>
        ),
    },
    {
      key: "status",
      header: "Status",
      width: "12%",
      cell: (r) => (
        <Badge tone={toneForStatus(r.status, r.conflict)}>
          {labelForStatus(r.status, r.conflict)}
        </Badge>
      ),
    },
    {
      key: "concierge",
      header: "Concierge",
      width: "10%",
      cell: (r) => <span className="muted">{r.concierge || "—"}</span>,
    },
    {
      key: "pickup",
      header: "Pickup",
      width: "8%",
      cell: (r) => <span className="muted">{r.pickup_location || "—"}</span>,
    },
  ];

  return (
    <div className="page">
      <div className="page__head page__head--row">
        <div>
          <h1 className="page__title">Bookings</h1>
          <div className="page__sub">
            Click any booking to view/edit/delete.
          </div>
        </div>

        <Link className="btn btn--primary" to="/admin/bookings/new">
          + New Booking
        </Link>
      </div>

      {loading && <div className="memMuted">Loading bookings…</div>}
      {loadErr && <div className="adminAuth__error" style={{ marginTop: 10 }}>{loadErr}</div>}

      <Card
        title="Booking list"
        right={
          <div className="filters">
            <select
              className="select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              disabled={loading}
            >
              <option>All</option>
              <option>Requested</option>
              <option>Approved</option>
              <option>Active</option>
              <option>Completed</option>
              <option>Cancelled</option>
            </select>
          </div>
        }
      >
        <Table
  columns={columns}
  rows={rows}
  onRowClick={(r) => nav(`/admin/bookings/${r.id}`)}
/>
        <div className="memMuted" style={{ marginTop: 10 }}>
          Tip: click a row to open the booking detail page.
        </div>
      </Card>
    </div>
  );
}